import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type {
  AddRecruitCandidate,
  Competitor,
  CompetitorRecruitStatus,
  ContactMethod,
  PatchCompetitor,
  TechnicianType,
} from "@breeze/shared";
import { TopNav } from "./TopNav.js";
import { Section } from "./BusinessGrowth.js";
import {
  addRecruitCandidate,
  fetchRecruitCandidates,
  initiateCall,
  patchRecruitCandidate,
  sendRecruitmentEmail,
  sendRecruitmentWhatsApp,
} from "../api.js";

// ===========================================================================
// Technician Recruitment dashboard — Andzelika's "who do I message next" list.
//
// Built on B_TCOMPETITOR (same table that backs Area Analysis competitor
// density). A row joins this view when someone sets `added_by` — either via
// manual add here or the "+ Add to recruitment" button on Area Analysis.
//
// Layout is a grouped list, not a Kanban — Andzelika's job is "who's next,
// what do I say" and the spec's 10 drag-drop columns would slow her down.
// ===========================================================================

type Props = { userName: string };

const STATUS_LABELS: Record<CompetitorRecruitStatus, string> = {
  not_contacted: "Not contacted",
  contacted: "Contacted",
  interested: "Interested",
  rejected: "Not suitable",
  hired: "Active technician",
};

const TECH_TYPE_LABELS: Record<TechnicianType, string> = {
  previous_breeze: "Previous Breeze cleaner",
  competitor: "Competitor",
  independent: "Independent cleaner",
  career_changer: "Career changer",
  referral: "Referral",
  social_lead: "Social media lead",
  unknown: "Unknown",
};

const CONTACT_METHOD_LABELS: Record<ContactMethod, string> = {
  whatsapp: "WhatsApp",
  sms: "SMS",
  email: "Email",
  call: "Call",
  unknown: "Unknown",
};

// Landing page for prospective technicians. Currently the Next.js staging
// build (per project_nextjs_rebuild). Swap to the live URL once that
// rebuild cuts over — this single constant feeds every reply template.
const RECRUITMENT_LANDING_URL = "https://test.carpetcleaning.co.uk/professional";

// Message templates. Three categories:
//   'cold'  — first-touch outreach. Short, warm, ends with one direct ask.
//             No value-prop dump (front-loading the pitch tanks reply rate).
//   'bump'  — gentle reminder when the cold message hasn't been answered
//             after 2-4 days. Even shorter than the cold.
//   'reply' — used AFTER the candidate has responded with interest. Now is
//             when the value props earn their place: same-day pay, calendar-
//             only visibility, choose-your-postcodes.
// `{name}` is interpolated via the displayName helper:
//   - First name if known (e.g. "Mr Valentin Rucarean" → "Valentin")
//   - "Mr Surname" if only title+surname known
//   - "there" if neither known
type TemplateCategory = "cold" | "bump" | "reply";
type Template = {
  key: string;
  category: TemplateCategory;
  technicianType: TechnicianType | null;  // null = applies to anyone (used for reply templates)
  label: string;
  body: string;
};
const TEMPLATES: Template[] = [
  // Default cold message — Darren's wording. Used for existing cleaners,
  // competitors, social leads, referrals (anyone already running a
  // carpet-cleaning operation). Listed FIRST so it's the fallback for
  // technician_types without an explicit template.
  {
    key: "cold_default",
    category: "cold",
    technicianType: null,
    label: "Existing cleaner / competitor (default)",
    body:
      "Hi {name}, hope you're well 🙂\n\n" +
      "I'm Andzelika from Breeze Carpet Cleaners in London https://carpetcleaning.co.uk\n\n" +
      "We have been cleaning carpets for around 15 years and have more work in South West London than our current cleaner can cover.\n\n" +
      "Are you able to take on more carpet and upholstery cleaning work?",
  },
  {
    key: "cold_previous_breeze",
    category: "cold",
    technicianType: "previous_breeze",
    label: "Previous Breeze cleaner",
    body:
      "Hi {name}, hope you're well 🙂\n\n" +
      "Darren and I are rebuilding Breeze and looking for one strong cleaner to keep busy across London. I thought of you because we already know your work.\n\n" +
      "We have more work in South West London than our current cleaner can cover. Are you able to take some on?",
  },
  {
    key: "cold_career_changer",
    category: "cold",
    technicianType: "career_changer",
    label: "Career changer",
    body:
      "Hi {name}, hope you're well 🙂\n\n" +
      "I'm Andzelika from Breeze Carpet Cleaners in London https://carpetcleaning.co.uk\n\n" +
      "We're looking for a reliable, hard-working person to be trained as a professional carpet/upholstery cleaning technician. We supply training, work, and the kit you'd need.\n\n" +
      "Would you be open to a quick chat?",
  },
  // Bump — Darren's wording. Used 2-4 days after the cold if no reply.
  {
    key: "bump_default",
    category: "bump",
    technicianType: null,
    label: "Gentle nudge (no reply)",
    body:
      "Hi {name} 🙂\n\n" +
      "Just checking if you saw my message.\n\n" +
      "Please let me know if you are interested.",
  },
  // ---- Reply / follow-up templates ----------------------------------------
  // These come AFTER the candidate has expressed any interest. Now the
  // value props earn their place. Universal across technician_type — once
  // someone has replied, the pitch is the same regardless of how they came in.
  {
    key: "reply_how_it_works",
    category: "reply",
    technicianType: null,
    label: "How it works (with rates)",
    body:
      "Hi {name},\n\n" +
      "Thanks for getting back to me.\n\n" +
      "A few things about how it works:\n\n" +
      "• You still keep your own customers and jobs. We just help fill gaps in your diary.\n\n" +
      "• We try to group jobs close to your other work where possible.\n\n" +
      "• Fast payment. We pay you the same day the customer pays — Stripe splits the transaction automatically.\n\n" +
      "• Simple scheduling. You share your Outlook or Google calendar free/busy link so we can see when you are available (no private information).\n\n" +
      "• You choose which jobs to accept by clicking \"Approve\" in WhatsApp.\n\n" +
      "• We provide a calendar link so you can see Breeze jobs in your own calendar view.\n\n" +
      "• Most jobs are around £130 — https://www.carpetcleaning.co.uk/pricing.html\n\n" +
      "• We pay 60% of the job to you.\n\n" +
      "Happy to have a quick call to explain more and answer any questions if useful.",
  },
  {
    key: "reply_book_call",
    category: "reply",
    technicianType: null,
    label: "Book a call",
    body: "Great 🙂\n\nWhat time works for a quick 10-minute call?",
  },
  {
    key: "reply_hesitant",
    category: "reply",
    technicianType: null,
    label: "If they're hesitant",
    body:
      "Hi {name} 🙂\n\n" +
      "No problem at all.\n\n" +
      "You still do your own jobs for your customers. We try to book jobs when you have gaps in your day and in areas close to your other work.\n\n" +
      "We pay as soon as the customer pays, usually the same day.\n\n" +
      "Scheduling is simple too. You send us your free/busy calendar information (no private details) so we know when you are free.\n\n" +
      "You confirm the jobs you want by clicking \"Approve\" in WhatsApp and then we add the job to your calendar.\n\n" +
      "More information here:\n" +
      `${RECRUITMENT_LANDING_URL}\n\n` +
      "Happy to have a quick call to explain more 🙂",
  },
];

// ---------------------------------------------------------------------------
// Name extraction for {name} interpolation.
//   - "Mr Valentin Rucarean" → "Valentin"   (first name, drop title)
//   - "Mr Smith"             → "Mr Smith"   (no first name known, keep formal)
//   - "John"                 → "John"
//   - null / empty           → "there"      ("Hi there, ...")
const NAME_TITLES = new Set(["Mr","Mrs","Ms","Miss","Dr","Mr.","Mrs.","Ms.","Dr."]);
function displayName(c: Competitor): string {
  const raw = c.contactName?.trim();
  if (!raw) return "there";
  const parts = raw.split(/\s+/);
  if (parts.length >= 3 && NAME_TITLES.has(parts[0]!)) return parts[1]!;
  if (parts.length === 2 && NAME_TITLES.has(parts[0]!)) return raw;
  return parts[0]!;
}

function renderTemplate(c: Competitor, body: string): string {
  return body.replaceAll("{name}", displayName(c));
}

// ---------------------------------------------------------------------------
// Contact action URLs — one-tap shortcuts so Andzelika doesn't have to
// retype numbers or compose messages by hand.

function telUrl(c: Competitor): string | null {
  if (!c.phoneNormalised) return null;
  return `tel:${c.phoneNormalised}`;
}

// Gmail compose URL — opens a new draft in the user's logged-in Gmail
// account with the recipient, subject and body pre-filled. Works for any
// @carpetcleaning.co.uk Google account (the same one used for kanban login),
// so Andzelika just clicks → review → send.
function gmailComposeUrl(
  email: string,
  subject: string,
  body: string
): string {
  return (
    "https://mail.google.com/mail/?view=cm&fs=1" +
    `&to=${encodeURIComponent(email)}` +
    `&su=${encodeURIComponent(subject)}` +
    `&body=${encodeURIComponent(body)}`
  );
}

// Outwards we want surfaced first in "Up next" because of the current
// SW recruitment gap (project_tech_situation_2026-05-10).
const SW_CORE_OUTWARDS = new Set([
  "SW1","SW2","SW3","SW4","SW5","SW6","SW7","SW8","SW9","SW10",
  "SW11","SW12","SW13","SW14","SW15","SW16","SW17","SW18","SW19","SW20",
]);
const SW_ADJACENT_OUTWARDS = new Set([
  "W4","W6","W12","W13","W14",
  "TW7","TW8","TW9","TW10","TW11",
  "KT1","KT2","KT3","KT6",
  "SM1","SM2",
  "CR0","CR4",
]);

// Priority score for sorting within "Up next". Higher = contact first.
// Rationale:
//   - SW core is the urgent gap (capacity recruitment for SW area).
//   - WhatsApp-ready is one-tap; less friction = more outreach done.
//   - Email is the next-best when no mobile.
//   - Explicit "TOP PICK" / "STRONG" notes from research come through too.
function priorityScore(c: Competitor): number {
  let s = 0;
  if (c.postcodeOutward && SW_CORE_OUTWARDS.has(c.postcodeOutward)) s += 50;
  else if (c.postcodeOutward && SW_ADJACENT_OUTWARDS.has(c.postcodeOutward)) s += 20;
  if (c.preferredContactMethod === "whatsapp") s += 30;
  else if (c.preferredContactMethod === "email") s += 20;
  else if (c.preferredContactMethod === "call") s += 10;
  if (c.recruitNotes && /STRONG|TOP PICK/i.test(c.recruitNotes)) s += 20;
  if (c.phone || c.email) s += 5;  // anything > unreachable
  return s;
}

// ---------------------------------------------------------------------------

type Group = {
  key: string;
  label: string;
  subtitle: string;
  collapsedByDefault: boolean;
  match: (c: Competitor) => boolean;
};

function isFollowUpDue(c: Competitor): boolean {
  if (!c.nextFollowUpAt) return false;
  return new Date(c.nextFollowUpAt).getTime() <= Date.now();
}

const GROUPS: Group[] = [
  {
    key: "up_next",
    label: "Up next",
    subtitle: "Follow-ups due now + leads we haven't reached out to yet",
    collapsedByDefault: false,
    match: (c) => isFollowUpDue(c) || c.recruitStatus === "not_contacted",
  },
  {
    key: "awaiting",
    label: "Awaiting reply",
    subtitle: "We've messaged — no reply yet",
    collapsedByDefault: false,
    match: (c) => c.recruitStatus === "contacted" && !isFollowUpDue(c),
  },
  {
    key: "interested",
    label: "Interested / call or trial booked",
    subtitle: "Replied positively — keep warm",
    collapsedByDefault: false,
    match: (c) => c.recruitStatus === "interested" && !isFollowUpDue(c),
  },
  {
    key: "hired",
    label: "Active technicians",
    subtitle: "Hired through this pipeline",
    collapsedByDefault: true,
    match: (c) => c.recruitStatus === "hired",
  },
  {
    key: "rejected",
    label: "Not suitable / no response",
    subtitle: "Closed out",
    collapsedByDefault: true,
    match: (c) => c.recruitStatus === "rejected",
  },
];

// ---------------------------------------------------------------------------

export function Recruitment({ userName }: Props): JSX.Element {
  const q = useQuery<Competitor[]>({
    queryKey: ["recruitment", "candidates"],
    queryFn: fetchRecruitCandidates,
  });
  const [openId, setOpenId] = useState<number | null>(null);
  const [showHelp, setShowHelp] = useState(false);
  const [showScript, setShowScript] = useState(false);
  const [showAdd, setShowAdd] = useState(false);
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>(
    Object.fromEntries(GROUPS.map((g) => [g.key, g.collapsedByDefault]))
  );

  const grouped = useMemo(() => {
    const buckets = new Map<string, Competitor[]>();
    for (const g of GROUPS) buckets.set(g.key, []);
    for (const c of q.data ?? []) {
      for (const g of GROUPS) {
        if (g.match(c)) {
          buckets.get(g.key)!.push(c);
          break;
        }
      }
    }
    // Sort "Up next" by priority score (SW + WhatsApp-ready first).
    // Other buckets keep server-side order (status + last_contacted_at).
    const upNext = buckets.get("up_next");
    if (upNext) {
      upNext.sort((a, b) => {
        const diff = priorityScore(b) - priorityScore(a);
        if (diff !== 0) return diff;
        // Tie-break by created_at (firstSeenAt) — most recently added first.
        return new Date(b.firstSeenAt).getTime() - new Date(a.firstSeenAt).getTime();
      });
    }
    return buckets;
  }, [q.data]);

  const openCandidate = useMemo<Competitor | null>(() => {
    if (openId === null || !q.data) return null;
    return q.data.find((c) => c.id === openId) ?? null;
  }, [openId, q.data]);

  return (
    <div className="flex h-full flex-col bg-ink-800">
      <TopNav userName={userName} />
      <main className="flex-1 overflow-y-auto p-6">
        <div className="mx-auto max-w-7xl space-y-6">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div>
              <div className="text-[13px] font-semibold uppercase tracking-[0.18em] text-ink-100">
                Recruitment
              </div>
              <h1 className="mt-1 text-2xl font-semibold text-ink-50">
                Technician recruitment — who to contact next
              </h1>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setShowAdd(true)}
                className="rounded-md bg-aubergine px-3 py-1.5 text-[13px] font-semibold text-ink-50 hover:bg-aubergineLight"
              >
                + Add candidate
              </button>
              <button
                onClick={() => setShowScript(true)}
                className="rounded-md border border-ink-500 bg-ink-700 px-3 py-1.5 text-[13px] font-semibold text-ink-50 hover:bg-ink-600"
              >
                View phone script
              </button>
              <button
                onClick={() => setShowHelp(true)}
                className="rounded-md border border-ink-500 bg-ink-700 px-3 py-1.5 text-[13px] font-semibold text-ink-50 hover:bg-ink-600"
              >
                How to use
              </button>
            </div>
          </div>

          {showHelp && <HowToUseModal onClose={() => setShowHelp(false)} />}
          {showScript && <PhoneScriptModal onClose={() => setShowScript(false)} />}
          {showAdd && <AddCandidateModal onClose={() => setShowAdd(false)} />}

          {q.isLoading ? (
            <div className="text-[15px] text-ink-100">Loading…</div>
          ) : !q.data ? (
            <div className="text-[15px] text-ink-100">No candidates yet.</div>
          ) : (
            <div className="grid grid-cols-1 gap-6 xl:grid-cols-3">
              <div className="space-y-4 xl:col-span-2">
                {GROUPS.map((g) => {
                  const rows = grouped.get(g.key) ?? [];
                  if (rows.length === 0 && g.collapsedByDefault) return null;
                  const isCollapsed = collapsed[g.key];
                  return (
                    <Section
                      key={g.key}
                      title={`${g.label} (${rows.length})`}
                      subtitle={g.subtitle}
                      right={
                        <button
                          onClick={() => setCollapsed((c) => ({ ...c, [g.key]: !c[g.key] }))}
                          className="text-[13px] text-ink-100 hover:text-ink-50"
                        >
                          {isCollapsed ? "Show" : "Hide"}
                        </button>
                      }
                    >
                      {isCollapsed ? null : rows.length === 0 ? (
                        <div className="text-[14px] text-ink-100">— nobody here right now</div>
                      ) : (
                        <ul className="space-y-2">
                          {rows.map((c) => (
                            <CandidateRow
                              key={c.id}
                              c={c}
                              onOpen={() => setOpenId(c.id)}
                              selected={openId === c.id}
                            />
                          ))}
                        </ul>
                      )}
                    </Section>
                  );
                })}
              </div>
              <div className="xl:col-span-1">
                {openCandidate ? (
                  <CandidateDrawer
                    key={openCandidate.id}
                    c={openCandidate}
                    onClose={() => setOpenId(null)}
                  />
                ) : (
                  <Section title="Candidate detail" subtitle="Click any row to open">
                    <div className="text-[14px] text-ink-100">
                      Click a candidate on the left to see contact details, change status, copy a
                      message template, or set a follow-up date.
                    </div>
                  </Section>
                )}
              </div>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Row + drawer
// ---------------------------------------------------------------------------

const EMAIL_SUBJECT = "Carpet cleaning subcontract work in London";

function CandidateRow({
  c,
  onOpen,
  selected,
}: {
  c: Competitor;
  onOpen: () => void;
  selected: boolean;
}): JSX.Element {
  // Row title: prefer the full person name when known, else business name.
  // (displayName() helper returns first-name-only — that's for greetings,
  // not for the row label.)
  const rowTitle = c.contactName ?? c.businessName;
  const subtitle = [c.businessName !== rowTitle ? c.businessName : null, c.postcodeOutward]
    .filter(Boolean)
    .join(" · ");
  const followUpOverdue = isFollowUpDue(c);
  const isSwPriority =
    c.postcodeOutward !== null && SW_CORE_OUTWARDS.has(c.postcodeOutward);
  // Note: row-level Call + Email are now API-driven (CallButton +
  // SendEmailButton). The cold-message text is still rendered for the
  // drawer's template picker / FieldLinks via defaultColdMessage().
  return (
    <li
      className={`flex items-stretch gap-2 rounded-md border transition-colors ${
        selected
          ? "border-aubergineLight bg-aubergine/20"
          : "border-ink-700 bg-ink-800/40 hover:border-ink-500"
      }`}
    >
      <button
        onClick={onOpen}
        className="flex flex-1 items-center text-left hover:bg-ink-700/40 rounded-l-md px-3 py-2"
      >
        <div className="min-w-0 flex-1">
          <div className="flex items-baseline gap-2">
            <span className="truncate font-semibold text-ink-50">{rowTitle}</span>
            {isSwPriority && (
              <span className="rounded-sm bg-aubergine/40 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-ink-50">
                SW
              </span>
            )}
            {followUpOverdue && (
              <span className="rounded-sm bg-stage-lost/30 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-stage-lost">
                Follow up
              </span>
            )}
          </div>
          {subtitle && (
            <div className="truncate text-[12px] text-ink-100">{subtitle}</div>
          )}
          <div className="mt-1 flex flex-wrap items-baseline gap-3 text-[11px] text-ink-100">
            <span>{TECH_TYPE_LABELS[c.technicianType]}</span>
            <span>· {CONTACT_METHOD_LABELS[c.preferredContactMethod]}</span>
            <LineTypeBadge lineType={c.lineType} hasPhone={!!c.phoneNormalised} />
            {c.fitScore !== null && <span>· Fit {c.fitScore}</span>}
            {c.lastContactedAt && (
              <span>
                · Last contacted {new Date(c.lastContactedAt).toLocaleDateString("en-GB")}
              </span>
            )}
          </div>
        </div>
      </button>
      <div className="flex items-center gap-1 px-2">
        <SendWhatsAppButton c={c} />
        <CallButton c={c} />
        <SendEmailButton c={c} />
        <ActionIcon
          href={c.website}
          label="Website"
          title={c.website ? "Open website" : "No website on file"}
        >
          <svg viewBox="0 0 24 24" className="h-4 w-4 fill-current">
            <path d="M12 2a10 10 0 1 0 0 20 10 10 0 0 0 0-20zm6.93 6h-2.95a15.7 15.7 0 0 0-1.38-3.56A8.03 8.03 0 0 1 18.93 8zM12 4.04c.83 1.2 1.48 2.53 1.91 3.96h-3.82A13.7 13.7 0 0 1 12 4.04zM4.26 14a7.96 7.96 0 0 1 0-4h3.38a16.6 16.6 0 0 0-.14 2c0 .68.05 1.35.14 2H4.26zm.81 2h2.95c.32 1.25.78 2.45 1.38 3.56A7.99 7.99 0 0 1 5.07 16zm2.95-8H5.07a7.99 7.99 0 0 1 4.33-3.56C8.8 5.55 8.34 6.75 8.02 8zM12 19.96A13.7 13.7 0 0 1 10.09 16h3.82A13.7 13.7 0 0 1 12 19.96zM14.34 14H9.66a14.36 14.36 0 0 1 0-4h4.68a14.36 14.36 0 0 1 0 4zm.26 5.56c.6-1.11 1.06-2.31 1.38-3.56h2.95a8.03 8.03 0 0 1-4.33 3.56zM16.36 14c.09-.65.14-1.32.14-2s-.05-1.35-.14-2h3.38a7.96 7.96 0 0 1 0 4h-3.38z"/>
          </svg>
        </ActionIcon>
      </div>
    </li>
  );
}

// Twilio LineTypeIntelligence badge. Tells Andzelika at a glance which
// channels this candidate's number supports:
//   📱 mobile       — SMS works, WhatsApp very likely
//   ☎️ landline     — no SMS; WhatsApp only if they registered Business
//   📡 nonFixedVoip — Google Voice / similar; SMS varies; WhatsApp possible
//   ❓ unchecked    — phone exists but we haven't looked it up yet
function LineTypeBadge({
  lineType,
  hasPhone,
}: {
  lineType: string | null;
  hasPhone: boolean;
}): JSX.Element | null {
  if (!hasPhone) return null;
  let glyph = "❓";
  let label = "unchecked";
  let tone = "text-ink-100/70";
  if (lineType === "mobile") {
    glyph = "📱";
    label = "mobile";
    tone = "text-slack-green";
  } else if (lineType === "landline") {
    glyph = "☎️";
    label = "landline";
    tone = "text-ink-100";
  } else if (lineType === "nonFixedVoip" || lineType === "fixedVoip") {
    glyph = "📡";
    label = "VoIP";
    tone = "text-slack-cyan";
  } else if (lineType) {
    glyph = "❓";
    label = lineType;
  }
  return (
    <span className={tone} title={`Line type: ${label}`}>
      · {glyph} {label}
    </span>
  );
}

// Shared icon-button that drives an async action with idle/active/done/
// error states. Used for the row-level WhatsApp send, click-to-call, and
// email send — all of which call an API and want consistent visual
// feedback (sending → green tick → fade back).
type AsyncPhase = "idle" | "active" | "done" | "error";
function AsyncIconButton({
  enabled,
  ariaLabel,
  titleByPhase,
  iconByPhase,
  activeTone,
  confirm,
  action,
}: {
  enabled: boolean;
  ariaLabel: string;
  titleByPhase: Record<AsyncPhase, string>;
  iconByPhase: Record<AsyncPhase, JSX.Element>;
  activeTone?: string;  // optional override for the "active" state colour
  confirm?: () => boolean;  // synchronous confirmation prompt
  action: () => Promise<void>;
}): JSX.Element {
  const [phase, setPhase] = useState<AsyncPhase>("idle");
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  async function handleClick(e: React.MouseEvent): Promise<void> {
    e.stopPropagation();
    if (!enabled || phase === "active") return;
    if (confirm && !confirm()) return;
    setPhase("active");
    setErrorMsg(null);
    try {
      await action();
      setPhase("done");
      setTimeout(() => setPhase("idle"), 2000);
    } catch (err) {
      setErrorMsg(err instanceof Error ? err.message : "failed");
      setPhase("error");
      setTimeout(() => setPhase("idle"), 4000);
    }
  }

  const tone =
    phase === "done"
      ? "bg-slack-green/30 text-slack-green"
      : phase === "error"
        ? "bg-stage-lost/30 text-stage-lost"
        : phase === "active"
          ? (activeTone ?? "bg-aubergine/40 text-ink-50")
          : enabled
            ? "text-ink-50 hover:bg-aubergine"
            : "text-ink-100/30 cursor-not-allowed";
  const title =
    phase === "error" && errorMsg
      ? `Failed: ${errorMsg}`
      : titleByPhase[phase];
  return (
    <button
      onClick={(e) => void handleClick(e)}
      disabled={!enabled || phase === "active"}
      aria-label={ariaLabel}
      title={title}
      className={`inline-flex h-7 w-7 items-center justify-center rounded-md transition-colors ${tone}`}
    >
      {iconByPhase[phase]}
    </button>
  );
}

const SPINNER_ICON = (
  <svg viewBox="0 0 24 24" className="h-4 w-4 animate-spin fill-current">
    <path d="M12 2v4a8 8 0 0 1 8 8h4a12 12 0 0 0-12-12z" />
  </svg>
);
const TICK_ICON = (
  <svg viewBox="0 0 24 24" className="h-4 w-4 fill-current">
    <path d="M9 16.2l-3.5-3.5L4 14.2 9 19.2 20 8.2l-1.5-1.5z" />
  </svg>
);

const WHATSAPP_GLYPH = (
  <svg viewBox="0 0 24 24" className="h-4 w-4 fill-current">
    <path d="M19.05 4.91A9.82 9.82 0 0 0 12.04 2c-5.46 0-9.91 4.45-9.91 9.91 0 1.75.46 3.45 1.32 4.95L2.05 22l5.25-1.38a9.9 9.9 0 0 0 4.74 1.21h.01c5.46 0 9.91-4.45 9.91-9.91 0-2.65-1.03-5.14-2.91-7.01zM12.05 20.15h-.01a8.23 8.23 0 0 1-4.2-1.15l-.3-.18-3.12.82.83-3.04-.2-.31a8.18 8.18 0 0 1-1.26-4.38c0-4.54 3.7-8.23 8.25-8.23 2.2 0 4.27.86 5.83 2.42a8.18 8.18 0 0 1 2.41 5.83c.01 4.54-3.69 8.22-8.23 8.22z" />
  </svg>
);
const PHONE_GLYPH = (
  <svg viewBox="0 0 24 24" className="h-4 w-4 fill-current">
    <path d="M6.62 10.79a15.05 15.05 0 0 0 6.59 6.59l2.2-2.2a1 1 0 0 1 1.02-.24c1.12.37 2.33.57 3.57.57a1 1 0 0 1 1 1V20a1 1 0 0 1-1 1A17 17 0 0 1 3 4a1 1 0 0 1 1-1h3.5a1 1 0 0 1 1 1c0 1.24.2 2.45.57 3.57a1 1 0 0 1-.24 1.02l-2.21 2.2z" />
  </svg>
);
const EMAIL_GLYPH = (
  <svg viewBox="0 0 24 24" className="h-4 w-4 fill-current">
    <path d="M20 4H4a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V6a2 2 0 0 0-2-2zm0 4-8 5-8-5V6l8 5 8-5z" />
  </svg>
);

// Send the approved cleaner_reach_out WhatsApp Content template via the
// Twilio Content API. Creates the tech kanban card on first send so the
// conversation thread is captured from message 1.
function SendWhatsAppButton({ c }: { c: Competitor }): JSX.Element {
  const qc = useQueryClient();
  // Any UK number is fair game — some businesses (Marcus at XS Clean)
  // register WhatsApp Business on a landline. Twilio returns failed-
  // delivery in the Timeline if the number isn't on WhatsApp. The
  // line-type badge gives Andzelika a heuristic for whether to expect
  // success before clicking.
  const enabled =
    c.phoneNormalised !== null && c.phoneNormalised.startsWith("+44");
  const displayName = c.contactName ?? c.businessName;
  return (
    <AsyncIconButton
      enabled={enabled}
      ariaLabel="Send WhatsApp"
      titleByPhase={{
        idle: enabled
          ? "Send approved cleaner_reach_out WhatsApp template"
          : "No UK mobile on file",
        active: "Sending…",
        done: "Sent ✓",
        error: "Failed",
      }}
      iconByPhase={{
        idle: WHATSAPP_GLYPH,
        active: SPINNER_ICON,
        done: TICK_ICON,
        error: WHATSAPP_GLYPH,
      }}
      confirm={() =>
        window.confirm(
          `Send the approved WhatsApp template to ${displayName} (${c.phoneNormalised})?`
        )
      }
      action={async () => {
        await sendRecruitmentWhatsApp(c.id);
        void qc.invalidateQueries({ queryKey: ["recruitment", "candidates"] });
      }}
    />
  );
}

// Click-to-call: posts to /api/voice/click-to-call (same backend as the
// Account-tab PhoneLink — rings the staff SIP endpoint via Twilio, then
// bridges the candidate's number with Breeze caller ID).
function CallButton({ c }: { c: Competitor }): JSX.Element {
  const enabled = Boolean(c.phoneNormalised && c.phoneNormalised.length > 0);
  const number = c.phoneNormalised ?? "";
  return (
    <AsyncIconButton
      enabled={enabled}
      ariaLabel="Call"
      titleByPhase={{
        idle: enabled
          ? `Click to ring your softphone, then bridge ${number}`
          : "No phone on file",
        active: "Calling your softphone…",
        done: "Ringing ✓",
        error: "Failed",
      }}
      iconByPhase={{
        idle: PHONE_GLYPH,
        active: SPINNER_ICON,
        done: TICK_ICON,
        error: PHONE_GLYPH,
      }}
      activeTone="bg-slack-cyan/30 text-slack-cyan"
      action={async () => {
        await initiateCall(number);
      }}
    />
  );
}

// Send the cold-recruitment email via Gmail (info@carpetcleaning.co.uk).
// Mirrors SendWhatsAppButton — API path creates the tech card on first
// send so the email thread is captured in kanban from message 1.
function SendEmailButton({ c }: { c: Competitor }): JSX.Element {
  const qc = useQueryClient();
  const enabled = Boolean(c.email && c.email.trim().length > 0);
  const displayName = c.contactName ?? c.businessName;
  return (
    <AsyncIconButton
      enabled={enabled}
      ariaLabel="Send email"
      titleByPhase={{
        idle: enabled
          ? "Send cold-recruitment email from info@carpetcleaning.co.uk"
          : "No email on file",
        active: "Sending…",
        done: "Sent ✓",
        error: "Failed",
      }}
      iconByPhase={{
        idle: EMAIL_GLYPH,
        active: SPINNER_ICON,
        done: TICK_ICON,
        error: EMAIL_GLYPH,
      }}
      confirm={() =>
        window.confirm(
          `Send the cold-recruitment email to ${displayName} (${c.email})?\n\nFrom: info@carpetcleaning.co.uk`
        )
      }
      action={async () => {
        await sendRecruitmentEmail(c.id);
        void qc.invalidateQueries({ queryKey: ["recruitment", "candidates"] });
      }}
    />
  );
}

function ActionIcon({
  href,
  label,
  title,
  children,
}: {
  href: string | null;
  label: string;
  title: string;
  children: React.ReactNode;
}): JSX.Element {
  const disabled = !href;
  const className = `inline-flex h-7 w-7 items-center justify-center rounded-md transition-colors ${
    disabled
      ? "text-ink-100/30 cursor-not-allowed"
      : "text-ink-50 hover:bg-aubergine hover:text-ink-50"
  }`;
  if (disabled) {
    return (
      <span className={className} aria-label={label} title={title}>
        {children}
      </span>
    );
  }
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      onClick={(e) => e.stopPropagation()}
      className={className}
      aria-label={label}
      title={title}
    >
      {children}
    </a>
  );
}

function CandidateDrawer({
  c,
  onClose,
}: {
  c: Competitor;
  onClose: () => void;
}): JSX.Element {
  const qc = useQueryClient();
  const patch = useMutation({
    mutationFn: (body: PatchCompetitor) => patchRecruitCandidate(c.id, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["recruitment", "candidates"] }),
  });

  const [notes, setNotes] = useState(c.recruitNotes ?? "");
  const [followUp, setFollowUp] = useState(
    c.nextFollowUpAt ? new Date(c.nextFollowUpAt).toISOString().slice(0, 10) : ""
  );
  // Pick the most useful default tab based on where the candidate is in
  // the funnel:
  //   - not_contacted → cold (first message)
  //   - contacted → bump (they got the cold but haven't replied yet)
  //   - interested / anything else → reply (now's value-prop time)
  const initialCategory: TemplateCategory =
    c.recruitStatus === "not_contacted"
      ? "cold"
      : c.recruitStatus === "contacted"
        ? "bump"
        : "reply";
  const [templateCategory, setTemplateCategory] = useState<TemplateCategory>(initialCategory);
  const initialTemplateKey =
    TEMPLATES.find(
      (t) => t.category === initialCategory && t.technicianType === c.technicianType
    )?.key ??
    TEMPLATES.find((t) => t.category === initialCategory)?.key ??
    TEMPLATES[0]!.key;
  const [templateKey, setTemplateKey] = useState<string>(initialTemplateKey);
  const [copied, setCopied] = useState(false);

  const visibleTemplates = TEMPLATES.filter((t) => t.category === templateCategory);
  const template =
    visibleTemplates.find((t) => t.key === templateKey) ??
    visibleTemplates[0] ??
    TEMPLATES[0]!;
  const rendered = renderTemplate(c, template.body);

  function switchCategory(cat: TemplateCategory): void {
    setTemplateCategory(cat);
    // Auto-pick a sensible default in the new category.
    const next =
      TEMPLATES.find(
        (t) => t.category === cat && t.technicianType === c.technicianType
      )?.key ?? TEMPLATES.find((t) => t.category === cat)?.key;
    if (next) setTemplateKey(next);
  }

  async function handleCopy(): Promise<void> {
    try {
      await navigator.clipboard.writeText(rendered);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // Clipboard can be blocked in some browser contexts — fall back to a prompt.
      // eslint-disable-next-line no-alert
      window.prompt("Copy the message manually:", rendered);
    }
  }

  return (
    <Section
      title={c.contactName ?? c.businessName}
      subtitle={
        c.contactName && c.contactName !== c.businessName ? c.businessName : undefined
      }
      right={
        <button
          onClick={onClose}
          className="text-[13px] text-ink-100 hover:text-ink-50"
        >
          Close
        </button>
      }
    >
      <div className="space-y-4 text-[13px]">
        {/* Contact details */}
        <div className="space-y-2">
          <DetailField
            label="Contact name"
            value={c.contactName ?? ""}
            onSave={(v) => patch.mutate({ contactName: v || null })}
          />
          <DetailField
            label="Phone"
            value={c.phone ?? ""}
            onSave={(v) => patch.mutate({ phone: v || null })}
            actions={<FieldLink href={telUrl(c)} label="Call" />}
          />
          <DetailField
            label="Email"
            value={c.email ?? ""}
            onSave={(v) => patch.mutate({ email: v || null })}
            actions={
              c.email ? (
                <FieldLink
                  href={gmailComposeUrl(c.email, EMAIL_SUBJECT, rendered)}
                  label="Gmail"
                />
              ) : null
            }
          />
          <DetailField
            label="Website"
            value={c.website ?? ""}
            onSave={(v) => patch.mutate({ website: v || null })}
            actions={c.website ? <FieldLink href={c.website} label="Open" /> : null}
          />
          <DetailField
            label="Social"
            value={c.socialUrl ?? ""}
            onSave={(v) => patch.mutate({ socialUrl: v || null })}
            actions={c.socialUrl ? <FieldLink href={c.socialUrl} label="Open" /> : null}
          />
          <DetailField
            label="Fit score (0-100)"
            value={c.fitScore !== null ? String(c.fitScore) : ""}
            onSave={(v) => {
              const n = v.trim() === "" ? null : Number(v);
              if (n !== null && (!Number.isFinite(n) || n < 0 || n > 100)) return;
              patch.mutate({ fitScore: n });
            }}
          />
        </div>

        {/* Status + type + channel */}
        <div className="grid grid-cols-2 gap-2">
          <SelectField
            label="Status"
            value={c.recruitStatus}
            options={Object.entries(STATUS_LABELS) as Array<[CompetitorRecruitStatus, string]>}
            onChange={(v) => patch.mutate({ recruitStatus: v })}
          />
          <SelectField
            label="Channel"
            value={c.preferredContactMethod}
            options={Object.entries(CONTACT_METHOD_LABELS) as Array<[ContactMethod, string]>}
            onChange={(v) => patch.mutate({ preferredContactMethod: v })}
          />
          <SelectField
            label="Type"
            value={c.technicianType}
            options={Object.entries(TECH_TYPE_LABELS) as Array<[TechnicianType, string]>}
            onChange={(v) => {
              patch.mutate({ technicianType: v });
              // Re-pick a template that matches the new technician_type in
              // the current category (cold or reply). Reply templates have
              // technicianType: null so they always survive a type change.
              const next =
                TEMPLATES.find(
                  (t) => t.category === templateCategory && t.technicianType === v
                )?.key ??
                TEMPLATES.find((t) => t.category === templateCategory)?.key;
              if (next) setTemplateKey(next);
            }}
          />
          <FollowUpField
            value={followUp}
            onChange={(v) => {
              setFollowUp(v);
              const iso = v ? new Date(`${v}T09:00:00Z`).toISOString() : null;
              patch.mutate({ nextFollowUpAt: iso });
            }}
          />
        </div>

        {/* Mark as contacted */}
        <div className="rounded-md border border-ink-700 bg-ink-800/40 p-3">
          <button
            onClick={() => patch.mutate({ recruitStatus: "contacted", lastContactedAt: "now" })}
            className="w-full rounded-md bg-aubergine py-1.5 font-semibold text-ink-50 hover:bg-aubergineLight"
          >
            Mark as contacted now
          </button>
          {c.lastContactedAt && (
            <div className="mt-1.5 text-[11px] text-ink-100">
              Last contacted {new Date(c.lastContactedAt).toLocaleString("en-GB")}
            </div>
          )}
        </div>

        {/* Notes */}
        <div>
          <label className="text-[11px] font-semibold uppercase tracking-wider text-ink-100">
            Notes
          </label>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            onBlur={() => {
              if (notes !== (c.recruitNotes ?? "")) {
                patch.mutate({ recruitNotes: notes.trim() || null });
              }
            }}
            rows={3}
            placeholder="Why this person looks promising, concerns, recent messages…"
            className="mt-1 w-full rounded-md border border-ink-500 bg-ink-700 px-2 py-1 text-[13px] text-ink-50"
          />
        </div>

        {/* Template picker — two tabs:
            • Cold message: short first-touch, ends with "open to a chat?".
            • After they reply: value props (same-day pay, calendar privacy,
              choose-your-postcodes) once interest is confirmed. */}
        <div className="rounded-md border border-ink-700 bg-ink-800/40 p-3">
          <div className="flex items-center gap-1 rounded-md border border-ink-500 bg-ink-700 p-0.5">
            <CategoryTab
              label="Cold"
              active={templateCategory === "cold"}
              onClick={() => switchCategory("cold")}
            />
            <CategoryTab
              label="Bump"
              active={templateCategory === "bump"}
              onClick={() => switchCategory("bump")}
            />
            <CategoryTab
              label="After reply"
              active={templateCategory === "reply"}
              onClick={() => switchCategory("reply")}
            />
          </div>
          <div className="mt-2 flex items-center justify-between gap-2">
            <label className="text-[11px] font-semibold uppercase tracking-wider text-ink-100">
              Template
            </label>
            <select
              value={templateKey}
              onChange={(e) => setTemplateKey(e.target.value)}
              className="rounded-md border border-ink-500 bg-ink-700 px-2 py-1 text-[12px] text-ink-50"
            >
              {visibleTemplates.map((t) => (
                <option key={t.key} value={t.key}>
                  {t.label}
                </option>
              ))}
            </select>
          </div>
          <pre className="mt-2 whitespace-pre-wrap rounded-md bg-ink-900/60 p-2 text-[12px] text-ink-50">
            {rendered}
          </pre>
          <div className="mt-2 flex justify-end">
            <button
              onClick={() => void handleCopy()}
              className="rounded-md bg-aubergine px-3 py-1 text-[12px] font-semibold text-ink-50 hover:bg-aubergineLight"
            >
              {copied ? "Copied ✓" : "Copy message"}
            </button>
          </div>
        </div>

        {c.addedBy && (
          <div className="text-[11px] text-ink-100/80">
            Added by {c.addedBy}
            {c.source !== "manual" && ` · sourced from ${c.source}`}
          </div>
        )}
      </div>
    </Section>
  );
}

function CategoryTab({
  label,
  active,
  onClick,
}: {
  label: string;
  active: boolean;
  onClick: () => void;
}): JSX.Element {
  return (
    <button
      onClick={onClick}
      className={`flex-1 rounded-md px-2 py-1 text-[12px] font-semibold transition-colors ${
        active ? "bg-aubergine text-ink-50" : "text-ink-100 hover:bg-ink-600 hover:text-ink-50"
      }`}
    >
      {label}
    </button>
  );
}

function DetailField({
  label,
  value,
  onSave,
  actions,
}: {
  label: string;
  value: string;
  onSave: (v: string) => void;
  actions?: React.ReactNode;
}): JSX.Element {
  const [local, setLocal] = useState(value);
  return (
    <div className="flex items-baseline gap-2">
      <label className="w-28 shrink-0 text-[11px] font-semibold uppercase tracking-wider text-ink-100">
        {label}
      </label>
      <input
        value={local}
        onChange={(e) => setLocal(e.target.value)}
        onBlur={() => {
          if (local !== value) onSave(local);
        }}
        className="flex-1 rounded-md border border-ink-500 bg-ink-700 px-2 py-1 text-[13px] text-ink-50"
      />
      {actions && <div className="flex items-center gap-1">{actions}</div>}
    </div>
  );
}

// Small text-button link beside a DetailField input (WA / Call / Gmail / Open).
function FieldLink({
  href,
  label,
}: {
  href: string | null;
  label: string;
}): JSX.Element | null {
  if (!href) return null;
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="rounded-md border border-aubergineLight px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-aubergineLight hover:bg-aubergine hover:text-ink-50"
    >
      {label}
    </a>
  );
}

function SelectField<T extends string>({
  label,
  value,
  options,
  onChange,
}: {
  label: string;
  value: T;
  options: Array<[T, string]>;
  onChange: (v: T) => void;
}): JSX.Element {
  return (
    <div>
      <label className="text-[11px] font-semibold uppercase tracking-wider text-ink-100">
        {label}
      </label>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value as T)}
        className="mt-1 w-full rounded-md border border-ink-500 bg-ink-700 px-2 py-1 text-[13px] text-ink-50"
      >
        {options.map(([v, l]) => (
          <option key={v} value={v}>
            {l}
          </option>
        ))}
      </select>
    </div>
  );
}

function FollowUpField({
  value,
  onChange,
}: {
  value: string;
  onChange: (v: string) => void;
}): JSX.Element {
  return (
    <div>
      <label className="text-[11px] font-semibold uppercase tracking-wider text-ink-100">
        Follow up on
      </label>
      <input
        type="date"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="mt-1 w-full rounded-md border border-ink-500 bg-ink-700 px-2 py-1 text-[13px] text-ink-50"
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Add-candidate modal
// ---------------------------------------------------------------------------

function AddCandidateModal({ onClose }: { onClose: () => void }): JSX.Element {
  const qc = useQueryClient();
  const [form, setForm] = useState<AddRecruitCandidate>({
    businessName: "",
    contactName: "",
    postcodeOutward: "",
    phone: "",
    email: "",
    website: "",
    socialUrl: "",
    technicianType: "unknown",
    preferredContactMethod: "whatsapp",
    recruitNotes: "",
  });
  const mut = useMutation({
    mutationFn: (body: AddRecruitCandidate) => addRecruitCandidate(body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["recruitment", "candidates"] });
      onClose();
    },
  });

  function setField<K extends keyof AddRecruitCandidate>(
    key: K,
    value: AddRecruitCandidate[K]
  ): void {
    setForm((f) => ({ ...f, [key]: value }));
  }

  function submit(): void {
    if (!form.businessName.trim()) return;
    const cleaned: AddRecruitCandidate = {
      businessName: form.businessName.trim(),
      contactName: form.contactName?.trim() || null,
      postcodeOutward: form.postcodeOutward?.trim() || null,
      phone: form.phone?.trim() || null,
      email: form.email?.trim() || null,
      website: form.website?.trim() || null,
      socialUrl: form.socialUrl?.trim() || null,
      technicianType: form.technicianType,
      preferredContactMethod: form.preferredContactMethod,
      recruitNotes: form.recruitNotes?.trim() || null,
    };
    mut.mutate(cleaned);
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-ink-900/70 p-6"
      onClick={onClose}
    >
      <div
        className="w-full max-w-xl rounded-xl border border-ink-700 bg-ink-800 p-6 shadow-card"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-baseline justify-between">
          <h2 className="text-xl font-semibold text-ink-50">Add a recruitment candidate</h2>
          <button onClick={onClose} className="text-[13px] text-ink-100 hover:text-ink-50">
            Cancel
          </button>
        </div>
        <p className="mt-1 text-[13px] text-ink-100">
          Only the business or person name is required — fill the rest in later.
        </p>
        <div className="mt-4 grid grid-cols-2 gap-3 text-[13px]">
          <FormInput
            label="Name (person)"
            value={form.contactName ?? ""}
            onChange={(v) => setField("contactName", v)}
          />
          <FormInput
            label="Business name *"
            value={form.businessName}
            onChange={(v) => setField("businessName", v)}
            required
          />
          <FormInput
            label="Area / outward (e.g. SW9)"
            value={form.postcodeOutward ?? ""}
            onChange={(v) => setField("postcodeOutward", v.toUpperCase())}
          />
          <FormInput
            label="Phone"
            value={form.phone ?? ""}
            onChange={(v) => setField("phone", v)}
          />
          <FormInput
            label="Email"
            value={form.email ?? ""}
            onChange={(v) => setField("email", v)}
          />
          <FormInput
            label="Website"
            value={form.website ?? ""}
            onChange={(v) => setField("website", v)}
          />
          <FormInput
            label="Social URL"
            value={form.socialUrl ?? ""}
            onChange={(v) => setField("socialUrl", v)}
          />
          <FormSelect
            label="Type"
            value={form.technicianType ?? "unknown"}
            options={Object.entries(TECH_TYPE_LABELS) as Array<[TechnicianType, string]>}
            onChange={(v) => setField("technicianType", v)}
          />
          <FormSelect
            label="Preferred channel"
            value={form.preferredContactMethod ?? "whatsapp"}
            options={Object.entries(CONTACT_METHOD_LABELS) as Array<[ContactMethod, string]>}
            onChange={(v) => setField("preferredContactMethod", v)}
          />
        </div>
        <div className="mt-3">
          <label className="text-[11px] font-semibold uppercase tracking-wider text-ink-100">
            Why this person looks promising
          </label>
          <textarea
            value={form.recruitNotes ?? ""}
            onChange={(e) => setField("recruitNotes", e.target.value)}
            rows={3}
            className="mt-1 w-full rounded-md border border-ink-500 bg-ink-700 px-2 py-1 text-[13px] text-ink-50"
          />
        </div>
        {mut.isError && (
          <div className="mt-2 text-[12px] text-stage-lost">
            Save failed — {(mut.error as Error).message}
          </div>
        )}
        <div className="mt-4 flex justify-end gap-2">
          <button
            onClick={onClose}
            className="rounded-md border border-ink-500 px-3 py-1.5 text-[13px] text-ink-50 hover:bg-ink-700"
          >
            Cancel
          </button>
          <button
            onClick={submit}
            disabled={!form.businessName.trim() || mut.isPending}
            className="rounded-md bg-aubergine px-3 py-1.5 text-[13px] font-semibold text-ink-50 hover:bg-aubergineLight disabled:opacity-40"
          >
            {mut.isPending ? "Saving…" : "Save candidate"}
          </button>
        </div>
      </div>
    </div>
  );
}

function FormInput({
  label,
  value,
  onChange,
  required,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  required?: boolean;
}): JSX.Element {
  return (
    <div>
      <label className="text-[11px] font-semibold uppercase tracking-wider text-ink-100">
        {label}
      </label>
      <input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        required={required}
        className="mt-1 w-full rounded-md border border-ink-500 bg-ink-700 px-2 py-1 text-[13px] text-ink-50"
      />
    </div>
  );
}

function FormSelect<T extends string>({
  label,
  value,
  options,
  onChange,
}: {
  label: string;
  value: T;
  options: Array<[T, string]>;
  onChange: (v: T) => void;
}): JSX.Element {
  return (
    <div>
      <label className="text-[11px] font-semibold uppercase tracking-wider text-ink-100">
        {label}
      </label>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value as T)}
        className="mt-1 w-full rounded-md border border-ink-500 bg-ink-700 px-2 py-1 text-[13px] text-ink-50"
      >
        {options.map(([v, l]) => (
          <option key={v} value={v}>
            {l}
          </option>
        ))}
      </select>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Help modal
// ---------------------------------------------------------------------------

function HowToUseModal({ onClose }: { onClose: () => void }): JSX.Element {
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-ink-900/70 p-6"
      onClick={onClose}
    >
      <div
        className="w-full max-w-2xl rounded-xl border border-ink-700 bg-ink-800 p-6 shadow-card"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-baseline justify-between">
          <h2 className="text-xl font-semibold text-ink-50">How to use the recruitment board</h2>
          <button onClick={onClose} className="text-[13px] text-ink-100 hover:text-ink-50">
            Close
          </button>
        </div>
        <div className="mt-4 space-y-4 text-[14px] text-ink-50">
          <section>
            <h3 className="font-semibold text-ink-50">The aim</h3>
            <p className="mt-1 text-ink-100">
              Andzelika needs to know who to message next and what to say. Darren adds promising
              technicians; Andzelika contacts them and moves each candidate forward. The goal is
              momentum, not perfect data — a name, area, and source link is enough to start.
            </p>
          </section>
          <section>
            <h3 className="font-semibold text-ink-50">Adding leads</h3>
            <p className="mt-1 text-ink-100">
              Two ways: the "+ Add candidate" button above for someone Darren found manually
              (Checkatrade, Facebook, referral), or the "+ Add to recruitment" button on the Area
              Analysis competitor side panel — useful for shortlisting cleaners we've already
              scraped from Google Places.
            </p>
          </section>
          <section>
            <h3 className="font-semibold text-ink-50">The groups</h3>
            <ul className="mt-1 list-disc pl-5 text-ink-100">
              <li>
                <strong>Up next</strong> — anyone with an overdue follow-up, plus anyone Andzelika
                hasn't messaged yet.
              </li>
              <li>
                <strong>Awaiting reply</strong> — messaged, no response yet. Set a follow-up date.
              </li>
              <li>
                <strong>Interested</strong> — replied positively. Book a call or trial.
              </li>
              <li>
                <strong>Active technicians</strong> / <strong>Not suitable</strong> — done.
              </li>
            </ul>
          </section>
          <section>
            <h3 className="font-semibold text-ink-50">Templates</h3>
            <p className="mt-1 text-ink-100">
              Click a candidate, pick the template that matches their type, hit "Copy message" and
              send it via WhatsApp, SMS, Instagram, Facebook, email, or phone. Then click "Mark as
              contacted now" to record the touchpoint. If they don't reply, follow up once after
              2-4 days; still nothing, mark them "Not suitable".
            </p>
          </section>
        </div>
      </div>
    </div>
  );
}

function PhoneScriptModal({ onClose }: { onClose: () => void }): JSX.Element {
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-ink-900/70 p-6"
      onClick={onClose}
    >
      <div
        className="max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-xl border border-ink-700 bg-ink-800 p-6 shadow-card"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-baseline justify-between">
          <h2 className="text-xl font-semibold text-ink-50">Phone script</h2>
          <button onClick={onClose} className="text-[13px] text-ink-100 hover:text-ink-50">
            Close
          </button>
        </div>
        <div className="mt-4 space-y-5 text-[14px] leading-relaxed text-ink-50">
          <section>
            <h3 className="text-[12px] font-semibold uppercase tracking-[0.18em] text-ink-100">
              Introduction
            </h3>
            <p className="mt-2 text-ink-50">
              Hi, my name is Andzelika from Breeze Carpet Cleaners in London.
            </p>
            <p className="mt-2 text-ink-50">
              We have been cleaning carpets for around 15 years and are looking for one or two more
              cleaners in your area.
            </p>
            <p className="mt-2 text-ink-50">
              We currently have more carpet and upholstery work than our cleaner can cover and
              wanted to see if you are able to take on more work.
            </p>
          </section>

          <section>
            <h3 className="text-[12px] font-semibold uppercase tracking-[0.18em] text-ink-100">
              Questions
            </h3>
            <ul className="mt-2 list-disc space-y-1 pl-5 text-ink-50">
              <li>Do you use a professional hot water extraction machine like Prochem or Ashbys?</li>
              <li>What areas can you cover?</li>
              <li>Do you have your own staff or do you subcontract?</li>
            </ul>
          </section>

          <section>
            <h3 className="text-[12px] font-semibold uppercase tracking-[0.18em] text-ink-100">
              How it works
            </h3>
            <p className="mt-2 text-ink-50">
              We try to book jobs when you have gaps in your day and in areas close to your other
              work.
            </p>
            <p className="mt-2 text-ink-50">Average job value is around £140.</p>
            <p className="mt-2 text-ink-50">Commission is 60%.</p>
            <p className="mt-2 text-ink-50">
              If we discount a job for the customer, Breeze covers the discount amount.
            </p>
            <p className="mt-2 text-ink-50">
              We pay you the same day the customer pays. Usually the same day if payment is by card
              or bank transfer (BACS).
            </p>
          </section>

          <section>
            <h3 className="text-[12px] font-semibold uppercase tracking-[0.18em] text-ink-100">
              Scheduling
            </h3>
            <p className="mt-2 text-ink-50">Do you use Outlook or Google Calendar?</p>
            <p className="mt-2 text-ink-50">
              You share your free/busy calendar information (no private information) so we know
              when you are available.
            </p>
            <p className="mt-2 text-ink-50">You approve jobs in WhatsApp.</p>
          </section>

          <section>
            <h3 className="text-[12px] font-semibold uppercase tracking-[0.18em] text-ink-100">
              Trial
            </h3>
            <p className="mt-2 text-ink-50">
              We can set you up on our system and send a trial job so you can see how we work.
            </p>
          </section>
        </div>
      </div>
    </div>
  );
}
